import { useState } from "react";
import { Play, KeyRound, ListMusic } from "lucide-react";
import { MODE_INFO, scaleForKey, modeMidis, triadsForKey } from "@/lib/theory";
import { Piano } from "@/components/Piano";
import { playScale } from "@/lib/audio";
import { useT, modeName, quality, sigLabel, keyNote, noteLabel } from "@/i18n";

export const KeyExplorer = ({ keys, testPrefix = "key" }) => {
  const { t, lang } = useT();
  const [keyId, setKeyId] = useState(keys[0].id);
  const [modeIdx, setModeIdx] = useState(0);
  const [playingMidi, setPlayingMidi] = useState(null);

  const key = keys.find((k) => k.id === keyId) || keys[0];
  const scale = scaleForKey(key);
  const pcs = scale.map((s) => s.pc);
  const rootPc = pcs[modeIdx];
  const midis = modeMidis(pcs, rootPc);
  const rootMidi = 48 + rootPc;
  const triads = triadsForKey(key);

  const playCurrent = () => {
    setPlayingMidi(null);
    playScale(midis, (m) => {
      setPlayingMidi(m);
      setTimeout(() => setPlayingMidi((p) => (p === m ? null : p)), 360);
    });
  };

  return (
    <div>
      {/* Key selector */}
      <div>
        <p className="step-tag text-background/60 mb-2">{t("keyx.pick")}</p>
        <div className="flex flex-wrap gap-2.5" data-testid={`${testPrefix}-selector`}>
          {keys.map((k) => {
            const isActive = k.id === keyId;
            return (
              <button
                key={k.id}
                data-testid={`${testPrefix}-${k.id}`}
                onClick={() => {
                  setKeyId(k.id);
                  setModeIdx(0);
                }}
                className={`font-display font-bold text-sm px-4 py-2.5 rounded-full border-2 transition-all ${
                  isActive
                    ? "bg-accent text-foreground border-accent -translate-y-0.5"
                    : "bg-transparent text-background/80 border-background/30 hover:border-background hover:-translate-y-0.5"
                }`}
                style={isActive ? { boxShadow: "4px 4px 0 0 rgba(253,251,247,0.9)" } : {}}
              >
                {k.id} {quality("Major", lang)}
              </button>
            );
          })}
        </div>
        <div className="mt-3 inline-flex items-center gap-2 bg-secondary text-secondary-foreground brutal-border rounded-full px-4 py-1.5">
          <KeyRound className="w-4 h-4" />
          <span className="step-tag" data-testid={`${testPrefix}-signature`}>{sigLabel(key.sig, lang)}</span>
        </div>
        {key.note && (
          <p className="mt-3 text-sm text-background/70 italic max-w-xl">{keyNote(key.id, key.note, lang)}</p>
        )}
      </div>

      {/* Mode pills */}
      <div className="mt-8">
        <p className="step-tag text-background/60 mb-2">{t("keyx.modes7")}</p>
        <div className="flex flex-wrap gap-2.5" data-testid={`${testPrefix}-mode-selector`}>
          {MODE_INFO.map((m, i) => {
            const isActive = i === modeIdx;
            return (
              <button
                key={m.name}
                data-testid={`${testPrefix}-mode-${i}`}
                onClick={() => setModeIdx(i)}
                className={`font-display font-bold text-sm px-4 py-2.5 rounded-full border-2 transition-all ${
                  isActive
                    ? "bg-accent text-foreground border-accent -translate-y-0.5"
                    : "bg-transparent text-background/80 border-background/30 hover:border-background hover:-translate-y-0.5"
                }`}
                style={isActive ? { boxShadow: "4px 4px 0 0 rgba(253,251,247,0.9)" } : {}}
              >
                <span className="opacity-50 mr-1.5">{i + 1}</span>
                {modeName(m.name, lang)}
                <span className="ml-1.5 opacity-70">{scale[i].name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Piano */}
      <div className="mt-8">
        <Piano activeMode={{ scale: midis, root: rootMidi }} playingMidi={playingMidi} />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-4">
        <button
          data-testid={`${testPrefix}-play-btn`}
          onClick={playCurrent}
          className="font-display font-bold bg-accent text-foreground px-5 py-3 rounded-full brutal-border hover:-translate-y-0.5 transition-transform flex items-center gap-2"
          style={{ boxShadow: "4px 4px 0 0 rgba(253,251,247,0.9)" }}
        >
          <Play className="w-4 h-4" /> {t("keyx.hear")} {scale[modeIdx].name} {modeName(MODE_INFO[modeIdx].name, lang)}
        </button>
        <div className="flex items-center gap-4 text-sm text-background/70">
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 rounded bg-primary inline-block" /> {t("common.startNote")}
          </span>
          <span className="flex items-center gap-2">
            <span className="w-4 h-4 rounded inline-block" style={{ background: "#66AEFF" }} /> {t("common.scaleNote")}
          </span>
        </div>
      </div>

      {/* Scale notes + triads */}
      <div className="mt-10 grid md:grid-cols-2 gap-5">
        <div
          className="bg-background text-foreground brutal-border rounded-2xl p-7"
          style={{ boxShadow: "6px 6px 0 0 rgba(253,251,247,0.9)" }}
        >
          <h3 className="font-display font-extrabold text-xl mb-1">
            {scale[modeIdx].name} {modeName(MODE_INFO[modeIdx].name, lang)}
            <span className="text-sm font-semibold text-muted-foreground ml-2">
              · {quality(MODE_INFO[modeIdx].quality, lang)}
            </span>
          </h3>
          <p className="step-tag text-muted-foreground mt-4 mb-2">{t("common.theNotes")}</p>
          <div className="flex flex-wrap gap-2" data-testid={`${testPrefix}-scale-notes`}>
            {midis.map((m, i) => (
              <span
                key={m}
                className={`font-display font-bold text-sm px-3 py-1.5 rounded-lg brutal-border ${
                  i === 0 ? "bg-primary text-primary-foreground" : "bg-muted"
                }`}
              >
                {noteLabel(i === midis.length - 1 ? scale[modeIdx].name : scale[(modeIdx + i) % 7].name, lang)}
              </span>
            ))}
          </div>
        </div>

        <div className="bg-secondary text-secondary-foreground brutal-border rounded-2xl p-7">
          <ListMusic className="w-6 h-6 mb-3" />
          <p className="step-tag opacity-80 mb-3">{t("keyx.chords6")}</p>
          <div className="grid grid-cols-2 gap-2" data-testid={`${testPrefix}-triads`}>
            {triads.map((c, i) => (
              <div key={i} className="bg-background/10 rounded-lg px-3 py-2">
                <p className="font-display font-bold text-sm flex items-center justify-between gap-2">
                  <span>{c.name} {quality(c.quality, lang)}</span>
                  <span className="opacity-70 font-semibold">{modeName(MODE_INFO[i].name, lang)}</span>
                </p>
                <p className="text-xs opacity-70">{c.notes.map((n) => noteLabel(n, lang)).join(" · ")}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
